import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getLangFromPath, buildPathWithLang } from '../i18n/lang';
import {
  fetchKnowledgeBaseArticle,
  fetchKnowledgeBaseCategories,
  fetchKnowledgeBaseCategoryArticles,
  searchKnowledgeBase,
} from '../lib/knowledgeBaseApi';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import './KnowledgeBasePage.css';

function KnowledgeBaseIcon({ icon }) {
  if (icon === 'bar-chart') {
    return <BarChartIcon fontSize="small" />;
  }

  if (icon === 'book-open') {
    return <MenuBookIcon fontSize="small" />;
  }

  return <span>•</span>;
}

function useCurrentLang() {
  const loc = useLocation();
  return getLangFromPath(loc.pathname);
}

function buildKbPath(lang, path) {
  return buildPathWithLang(lang, path);
}

const KB_TEXT = {
  uk: {
    title: 'База знань',
    hero: 'Простий довідник про Radiomonitor, метрики, рекламу, аудиторію та роботу системи.',
    searchPlaceholder: 'Пошук у базі знань',
    searchButton: 'Знайти',
    loading: 'Завантаження...',
    searching: 'Пошук...',
    error: 'Не вдалося завантажити базу знань.',
    searchResults: 'Результати пошуку',
    categories: 'Рубрики',
    back: '← База знань',
    categoryEyebrow: 'Рубрика',
    articles: 'Статті',
    noArticles: 'У цій рубриці поки немає статей.',
    categoryNotFound: 'Рубрику не знайдено.',
    articleEyebrow: 'Стаття',
    articleNotFound: 'Статтю не знайдено.',
    faq: 'FAQ',
  },
  en: {
    title: 'Knowledge Base',
    hero: 'A simple guide to Radiomonitor, metrics, advertising, audience analytics, and how the system works.',
    searchPlaceholder: 'Search the knowledge base',
    searchButton: 'Search',
    loading: 'Loading...',
    searching: 'Searching...',
    error: 'Could not load the knowledge base.',
    searchResults: 'Search results',
    categories: 'Categories',
    back: '← Knowledge Base',
    categoryEyebrow: 'Category',
    articles: 'Articles',
    noArticles: 'There are no articles in this category yet.',
    categoryNotFound: 'Category not found.',
    articleEyebrow: 'Article',
    articleNotFound: 'Article not found.',
    faq: 'FAQ',
  },
  it: {
    title: 'Base di conoscenza',
    hero: 'Una guida semplice a Radiomonitor, metriche, pubblicità, pubblico e funzionamento del sistema.',
    searchPlaceholder: 'Cerca nella base di conoscenza',
    searchButton: 'Cerca',
    loading: 'Caricamento...',
    searching: 'Ricerca...',
    error: 'Impossibile caricare la base di conoscenza.',
    searchResults: 'Risultati della ricerca',
    categories: 'Categorie',
    back: '← Base di conoscenza',
    categoryEyebrow: 'Categoria',
    articles: 'Articoli',
    noArticles: 'Non ci sono ancora articoli in questa categoria.',
    categoryNotFound: 'Categoria non trovata.',
    articleEyebrow: 'Articolo',
    articleNotFound: 'Articolo non trovato.',
    faq: 'FAQ',
  },
  fr: {
    title: 'Base de connaissances',
    hero: 'Un guide simple sur Radiomonitor, les métriques, la publicité, l’audience et le fonctionnement du système.',
    searchPlaceholder: 'Rechercher dans la base de connaissances',
    searchButton: 'Rechercher',
    loading: 'Chargement...',
    searching: 'Recherche...',
    error: 'Impossible de charger la base de connaissances.',
    searchResults: 'Résultats de recherche',
    categories: 'Rubriques',
    back: '← Base de connaissances',
    categoryEyebrow: 'Rubrique',
    articles: 'Articles',
    noArticles: 'Il n’y a pas encore d’articles dans cette rubrique.',
    categoryNotFound: 'Rubrique introuvable.',
    articleEyebrow: 'Article',
    articleNotFound: 'Article introuvable.',
    faq: 'FAQ',
  },
};

function kbText(lang) {
  return KB_TEXT[lang] || KB_TEXT.uk;
}

export function KnowledgeBasePage() {
  const lang = useCurrentLang();
  const t = kbText(lang);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('');

  useEffect(() => {
    let alive = true;

    async function load() {
      setStatus('loading');

      try {
        const data = await fetchKnowledgeBaseCategories(lang);
        if (!alive) return;
        setCategories(data.categories || []);
        setStatus('');
      } catch {
        if (!alive) return;
        setStatus('error');
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [lang]);

  async function handleSearch(event) {
    event.preventDefault();

    const value = query.trim();
    if (!value) {
      setResults([]);
      return;
    }

    setStatus('searching');

    try {
      const data = await searchKnowledgeBase(value, lang);
      setResults(data.results || []);
      setStatus('');
    } catch {
      setStatus('error');
    }
  }

  return (
    <main className="kbPage">
      <section className="kbHero">
        <p className="kbEyebrow">Radiomonitor</p>
        <h1>{t.title}</h1>
        <p>{t.hero}</p>

        <form className="kbSearch" onSubmit={handleSearch}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.searchPlaceholder}
          />
          <button type="submit">{t.searchButton}</button>
        </form>
      </section>

      {status === 'loading' ? <p className="kbStatus">{t.loading}</p> : null}
      {status === 'searching' ? <p className="kbStatus">{t.searching}</p> : null}
      {status === 'error' ? <p className="kbStatus kbError">{t.error}</p> : null}

      {results.length ? (
        <section className="kbSection">
          <h2>{t.searchResults}</h2>
          <div className="kbList">
            {results.map((item) => (
              <Link
                className="kbListItem"
                key={item.id}
                to={buildKbPath(lang, `/knowledge-base/articles/${item.slug}`)}
              >
                <span>{item.category_title || item.category_slug}</span>
                <strong>{item.title}</strong>
                {item.summary ? <p>{item.summary}</p> : null}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="kbSection">
        <h2>{t.categories}</h2>

        <div className="kbGrid">
          {categories.map((category) => (
            <Link
              className="kbCard"
              key={category.id}
              to={buildKbPath(lang, `/knowledge-base/${category.slug}`)}
            >
              <div className="kbCardHeader">
                <div className="kbIcon">
                  <KnowledgeBaseIcon icon={category.icon} />
                </div>
                <h3>{category.title}</h3>
              </div>
              {category.description ? <p>{category.description}</p> : null}
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}

export function KnowledgeBaseCategoryPage() {
  const lang = useCurrentLang();
  const t = kbText(lang);
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let alive = true;

    async function load() {
      setStatus('loading');

      try {
        const data = await fetchKnowledgeBaseCategoryArticles(categorySlug, lang);
        if (!alive) return;
        setCategory(data.category || null);
        setArticles(data.articles || []);
        setStatus('');
      } catch {
        if (!alive) return;
        setStatus('error');
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [categorySlug, lang]);

  return (
    <main className="kbPage">
      <Link className="kbBack" to={buildKbPath(lang, '/knowledge-base')}>
        {t.back}
      </Link>

      {status === 'loading' ? <p className="kbStatus">{t.loading}</p> : null}
      {status === 'error' ? <p className="kbStatus kbError">{t.categoryNotFound}</p> : null}

      {category ? (
        <>
          <section className="kbHero kbHeroSmall">
            <p className="kbEyebrow">{t.categoryEyebrow}</p>
            <h1>{category.title}</h1>
            {category.description ? <p>{category.description}</p> : null}
          </section>

          <section className="kbSection">
            <h2>{t.articles}</h2>

            <div className="kbList">
              {articles.map((article) => (
                <Link
                  className="kbListItem"
                  key={article.id}
                  to={buildKbPath(lang, `/knowledge-base/articles/${article.slug}`)}
                >
                  <strong>{article.title}</strong>
                  {article.summary ? <p>{article.summary}</p> : null}
                </Link>
              ))}

              {!articles.length ? <p className="kbStatus">{t.noArticles}</p> : null}
            </div>
          </section>
        </>
      ) : null}
    </main>
  );
}

export function KnowledgeBaseArticlePage() {
  const lang = useCurrentLang();
  const t = kbText(lang);
  const { articleSlug } = useParams();
  const [article, setArticle] = useState(null);
  const [faq, setFaq] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let alive = true;

    async function load() {
      setStatus('loading');

      try {
        const data = await fetchKnowledgeBaseArticle(articleSlug, lang);
        if (!alive) return;
        setArticle(data.article || null);
        setFaq(data.faq || []);
        setStatus('');
      } catch {
        if (!alive) return;
        setStatus('error');
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [articleSlug, lang]);

  return (
    <main className="kbPage">
      <Link className="kbBack" to={buildKbPath(lang, '/knowledge-base')}>
        {t.back}
      </Link>

      {status === 'loading' ? <p className="kbStatus">{t.loading}</p> : null}
      {status === 'error' ? <p className="kbStatus kbError">{t.articleNotFound}</p> : null}

      {article ? (
        <article className="kbArticle">
          <header>
            <p className="kbEyebrow">{t.articleEyebrow}</p>
            <h1>{article.title}</h1>
            {article.summary ? <p className="kbArticleSummary">{article.summary}</p> : null}
          </header>

          <div
            className="kbArticleContent"
            dangerouslySetInnerHTML={{ __html: article.content || '' }}
          />

          {faq.length ? (
            <section className="kbFaq">
              <h2>{t.faq}</h2>

              {faq.map((item) => (
                <details key={item.id}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </section>
          ) : null}
        </article>
      ) : null}
    </main>
  );
}
